import React from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem
} from '@heroui/react';
import { useAtomValue, useAtom } from 'jotai';
import type { CdnForm } from '@/store';
import { cdnProvidersQueryAtom, cdnProvidersListParamsAtom, cdnProvidersHydratedAtom } from '@/store';

interface CdnFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: CdnForm;
  setFormData: React.Dispatch<React.SetStateAction<CdnForm>>;
  isSubmitting: boolean;
  onSubmit: () => void;
  title: string;
  submitText: string;
}

const CDN_TAG_OPTIONS = [
  { value: 'stable', label: 'Stable' },
  { value: 'latest', label: 'Latest' },
  { value: 'exact', label: 'Exact' },
  { value: 'dev', label: 'Dev' },
  { value: 'beta', label: 'Beta' }
];

export function CdnFormModal({
  isOpen,
  onOpenChange,
  formData,
  setFormData,
  isSubmitting,
  onSubmit,
  title,
  submitText
}: CdnFormModalProps) {
  const [, setHydrated] = useAtom(cdnProvidersHydratedAtom);
  const { data: providersData, isLoading: isLoadingProviders } = useAtomValue(cdnProvidersQueryAtom);
  const providers = providersData?.data || [];

  // Enable query when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setHydrated(true);
    }
  }, [isOpen, setHydrated]);

  const isFormValid = React.useMemo(() => {
    // Name and CDN provider are required
    return !!formData.name && !!formData.cdn_provider_id;
  }, [formData.name, formData.cdn_provider_id]);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange} size="lg" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">{title}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                {/* Name */}
                <Input
                  label="Name"
                  placeholder="Enter CDN name"
                  value={formData.name}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
                  isRequired
                />

                {/* CDN Provider - Autocomplete */}
                <Autocomplete
                  label="CDN Provider"
                  placeholder="Select CDN provider"
                  defaultSelectedKey={formData.cdn_provider_id ? formData.cdn_provider_id.toString() : undefined}
                  onSelectionChange={(key) => {
                    if (key) {
                      const value = key as string;
                      setFormData((prev) => ({ ...prev, cdn_provider_id: parseInt(value) }));
                    }
                  }}
                  onOpenChange={() => {}} // Prevent modal from closing
                  isLoading={isLoadingProviders}
                  isRequired
                >
                  {providers.map((provider) => (
                    <AutocompleteItem key={provider.id.toString()}>{provider.name}</AutocompleteItem>
                  ))}
                </Autocomplete>

                {/* CDN URL */}
                <Input
                  label="CDN URL"
                  placeholder="Enter CDN URL"
                  value={formData.cdn_url}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, cdn_url: value }))}
                />

                {/* CDN Tag */}
                <Select
                  label="CDN Tag"
                  placeholder="Select CDN tag"
                  selectedKeys={[formData.cdn_tag]}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0] as 'stable' | 'latest' | 'exact' | 'dev' | 'beta';
                    setFormData((prev) => ({
                      ...prev,
                      cdn_tag: value,
                      version: value === 'latest' ? '' : prev.version
                    }));
                  }}
                  onOpenChange={() => {}} // Prevent modal from closing
                >
                  {CDN_TAG_OPTIONS.map((option) => (
                    <SelectItem key={option.value}>{option.label}</SelectItem>
                  ))}
                </Select>

                {/* Version */}
                <Input
                  label="Version"
                  placeholder="Enter version (e.g., 0.2.2)"
                  value={formData.version}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, version: value }))}
                />

                {/* Filename */}
                <Input
                  label="Filename"
                  placeholder="Enter filename"
                  value={formData.filename}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, filename: value }))}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                Cancel
              </Button>
              <Button color="primary" isLoading={isSubmitting} onPress={onSubmit} isDisabled={!isFormValid}>
                {submitText}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
